import * as React from 'react'

import { Box, Spinner } from '@factorialco/deprecated-design-system'
import { type SelectOption } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import {
  ContractsEmployeesContractVersionFragment,
  ContractsEmployeesEmployeeFragment,
  ContractsEmployeesReferenceContractFragment,
  TaskDetailAffecteeEmployeeFragment,
  TaskDetailAffecteeReferenceContractFragment,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Text from 'modules/core/components/Inputs/Text'
import MoneyField from 'modules/core/components/MoneyField'
import Select from 'modules/core/components/SelectField'
import i18n from 'modules/core/lib/i18n'

import { translations as roleLevelSalaryTranslations } from 'modules/contracts/components/FixFlow/lib/roleLevelSalaryTranslations'
import { ContractFlowType } from 'modules/contracts/components/FlowPage'
import FormWrapper from 'modules/contracts/components/FlowPage/FormWrapper'
import AdditionalCompensations from 'modules/contracts/components/FlowPage/Steps/RoleLevelSalary/_AdditionalCompensations'
import PayrollPolicy from 'modules/contracts/components/FlowPage/Steps/RoleLevelSalary/_PayrollPolicy'
import { useContractFlow } from 'modules/contracts/lib/contractFlowContext'
import { getFirstError } from 'modules/contracts/lib/errors'
import { translate } from 'modules/contracts/lib/translate'
import SalaryInRangeCallout from 'modules/employees/components/UnifiedAddEmployeeModal/ContractInformationStepV2/SalaryInRangeCallout'
import MatchingValueTooltip from 'modules/employees/components/UnifiedAddEmployeeModal/Fields/MatchingValueTooltip'
import { Salary as SalaryType } from 'modules/job_catalog/lib/salary'
import { getRecommendedSalaryValue, isValidSalaryValue } from 'modules/job_catalog/lib/salaryUtils'
import { useHasCompensations } from 'modules/payroll_bundle/hooks/useHasCompensations'

import SalaryHint from './_SalaryHint'

interface Props {
  form: Form
  step?: number
  withFormWrapper?: boolean
  type?: ContractFlowType
  canEditPayrollPolicy: boolean
  canSeeGrossSalary: boolean
  canSeeCompensations: boolean
  canSeePayrollPolicy: boolean
  translations?: { [key: string]: string }
  jobCatalogNodeSalary?: SalaryType
  jobCatalogTreeFeatureEnabled?: boolean
  legalEntityId: number
  legalEntityCountry: string
  employee:
    | ContractsEmployeesEmployeeFragment
    | TaskDetailAffecteeEmployeeFragment
    | null
    | undefined
  hasPayrollBundle: boolean
  contractVersion:
    | ContractsEmployeesReferenceContractFragment
    | ContractsEmployeesContractVersionFragment
    | TaskDetailAffecteeReferenceContractFragment
    | null
    | undefined
  cycleInfo?: React.ReactNode
}

const Salary = ({
  form,
  translations = roleLevelSalaryTranslations(),
  type = 'fix',
  step,
  canEditPayrollPolicy,
  canSeeGrossSalary,
  canSeeCompensations,
  canSeePayrollPolicy,
  withFormWrapper,
  jobCatalogNodeSalary,
  jobCatalogTreeFeatureEnabled,
  legalEntityId,
  legalEntityCountry,
  employee,
  hasPayrollBundle,
  contractVersion,
  cycleInfo,
}: Props) => {
  const { salaryRanges } = useContractFlow()
  const [isAmountFieldFocused, setIsAmountFieldFocused] = React.useState(false)
  const [isFrequencyFieldFocused, setIsFrequencyFieldFocused] = React.useState(false)
  const jobLevelId = form.get('job_catalog_level_id').value
  const salaryRange = salaryRanges.filter(
    (x) =>
      x.attributes.legal_entities_ids.includes(legalEntityId) &&
      x.attributes.level_id === Number(jobLevelId)
  )[0]

  const { hasCompensations, loading: hasCompensationsLoading } = useHasCompensations()

  const FREQUENCIES: Record<string, string> = {
    weekly: i18n.t('common.week.one'),
    daily: i18n.t('common.day.one'),
    monthly: i18n.t('common.month'),
    yearly: i18n.t('common.year'),
    hourly: i18n.t('common.hourly'),
  }
  const salaryOptions: SelectOption[] = Object.keys(FREQUENCIES).map((key) => ({
    label: FREQUENCIES[key] ?? '',
    value: key,
  }))
  const salaryAmountField = canSeeGrossSalary ? form.get('salary_amount') : undefined
  const salaryFrequencyField = canSeeGrossSalary ? form.get('salary_frequency') : undefined
  const salaryFrequency =
    !salaryFrequencyField ||
    salaryFrequencyField.value === '' ||
    salaryFrequencyField.value === null
      ? undefined
      : salaryFrequencyField.value
  const isSalarySectionFocused = isAmountFieldFocused || isFrequencyFieldFocused
  const isSalaryInRange =
    jobCatalogNodeSalary &&
    isValidSalaryValue(
      jobCatalogNodeSalary,
      Number(salaryAmountField?.value),
      salaryFrequencyField?.value || ''
    )

  const handleResetSalaryToRecommendedRange = () => {
    if (!jobCatalogNodeSalary) return
    const suggestedSalary = getRecommendedSalaryValue(jobCatalogNodeSalary)
    const newSalaryAmount = suggestedSalary ? `${suggestedSalary}` : ''

    salaryAmountField?.set(newSalaryAmount)
    salaryFrequencyField?.set(jobCatalogNodeSalary.periodicity)
  }

  const isFrench =
    contractVersion && type === 'fix'
      ? contractVersion.country === 'fr'
      : legalEntityCountry === 'fr'

  const salaryHint = () => {
    if (step && !jobCatalogTreeFeatureEnabled && salaryAmountField && salaryFrequencyField) {
      return (
        <SalaryHint
          step={step}
          type={type}
          salaryAmountField={salaryAmountField}
          salaryFrequencyField={salaryFrequencyField}
          isSalarySectionFocused={isSalarySectionFocused}
          legalEntityCountry={legalEntityCountry}
          salaryRange={salaryRange}
        />
      )
    }

    if (step && jobCatalogTreeFeatureEnabled && !!jobCatalogNodeSalary) {
      return (
        <Box maxWidth="s240" position="relative" width="full">
          <Box position="absolute" width="full">
            <SalaryInRangeCallout
              precedenceSalaryRange={jobCatalogNodeSalary}
              salaryAmount={Number(salaryAmountField?.value) || 0}
              salaryFrequency={salaryFrequencyField?.value || ''}
              roleLevelName={form.get('job_catalog_role_level_name')?.value || ''}
              legalEntityCountry={legalEntityCountry || ''}
              locationId={
                employee && 'defaultLocation' in employee
                  ? employee.defaultLocation?.id?.toString() ?? ''
                  : undefined
              }
            />
          </Box>
        </Box>
      )
    }

    return null
  }

  if (hasCompensationsLoading) {
    return (
      <Box
        gap="s8"
        padding="s48"
        alignItems="center"
        data-testid="additional-compensations-loading"
      >
        <Spinner size="s32" data-testid="loading" />
      </Box>
    )
  }

  return (
    <Box gap="s48">
      {!hasCompensations && canSeePayrollPolicy && (
        <PayrollPolicy
          translations={translations}
          canEdit={canEditPayrollPolicy}
          type={type}
          step={step}
          withFormWrapper={withFormWrapper}
          contractVersionId={contractVersion?.id?.toString()}
        />
      )}
      {hasCompensations && (
        <Box gap="s16">
          <Box flexDirection="row" paddingLeft="s24" paddingRight="s64">
            {cycleInfo}
          </Box>
        </Box>
      )}
      {canSeeGrossSalary && (
        <FormWrapper withFormWrapper={withFormWrapper} hint={salaryHint()}>
          <Box gap="s16">
            <Box flexDirection="row" gap="s16">
              <MoneyField
                value={salaryAmountField?.value}
                onChange={(value) => salaryAmountField?.set(value)}
                onFocus={() => setIsAmountFieldFocused(true)}
                onBlur={() => setIsAmountFieldFocused(false)}
                error={getFirstError(salaryAmountField)}
                label={translate(translations, 'base_salary')}
                currency={employee?.legalEntity?.currency ?? ''}
                mandatory={salaryFrequencyField?.hasValue}
              />
              <Select
                onChange={(value: string) => salaryFrequencyField?.set(value)}
                value={salaryFrequency}
                onFocus={() => setIsFrequencyFieldFocused(true)}
                onBlur={() => setIsFrequencyFieldFocused(false)}
                error={getFirstError(salaryFrequencyField)}
                label={translate(translations, 'salary_frequency')}
                none={i18n.t('common.choose')}
                options={salaryOptions}
                mandatory={salaryAmountField?.hasValue}
              />
              {jobCatalogNodeSalary && (
                <Box style={{ marginTop: '34px' }}>
                  <MatchingValueTooltip
                    type="salary"
                    linked={!!isSalaryInRange}
                    onClick={handleResetSalaryToRecommendedRange}
                  />
                </Box>
              )}
            </Box>
            {isFrench && form.has('fr_coefficient') && (
              <Text
                field={form.get('fr_coefficient')}
                label={i18n.t('contract_versions.forms.gross_salary.fr_coefficient')}
              />
            )}
          </Box>
        </FormWrapper>
      )}
      {canSeeCompensations && step && (
        <AdditionalCompensations
          step={step}
          type={type}
          translations={translations}
          isNewAdditionalCompensationsSupportEnabled={hasCompensations}
          form={form}
          legalEntityId={legalEntityId}
          hasPayrollBundle={hasPayrollBundle}
          employeeLegalEntityCurrency={employee?.legalEntity?.currency ?? ''}
          employeeLegalEntityId={employee?.legalEntity?.id ?? 0}
          contractVersion={contractVersion}
        />
      )}
    </Box>
  )
}

export default observer(Salary)
